import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckSquare, X, UserPlus, Users, Plus, ChevronRight } from 'lucide-react';
import { checklistsApi, projectsApi, usersApi, User, request } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useTelegram } from '../hooks/useTelegram';

export function CreateChecklistPage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();
  const { hapticFeedback, showAlert } = useTelegram();
  const queryClient = useQueryClient();

  // Принудительно обновляем данные пользователя при монтировании компонента
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
  }, [queryClient]);

  // Получаем данные текущего пользователя
  // Принудительно обновляем данные при загрузке страницы, чтобы получить актуальный статус isSystemAdmin
  const { data: currentUser, isLoading: userLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      try {
        return await usersApi.me();
      } catch (error: any) {
        // Игнорируем ошибки 404 - пользователь будет перенаправлен на страницу авторизации
        if (error?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 0, // Всегда считаем данные устаревшими
    gcTime: 0, // Не кешируем (gcTime заменил cacheTime в React Query v5)
    refetchOnMount: true, // Всегда обновляем при монтировании
    refetchOnWindowFocus: true, // Обновляем при фокусе окна
    retry: false, // Не повторяем запрос при ошибке
  });

  // Проверяем, является ли пользователь руководителем
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      try {
        return await projectsApi.list();
      } catch (error: any) {
        // Игнорируем ошибки 404 - просто возвращаем пустой массив
        if (error?.status === 404) {
          return [];
        }
        throw error;
      }
    },
    retry: false, // Не повторяем запрос при ошибке
  });

  // Системный админ или менеджер в проектах
  // Важно: проверяем isSystemAdmin строго на true, так как может быть undefined
  // Также проверяем роль SYSTEM_ADMIN в проектах для системных админов
  const isSystemAdminFromUser = Boolean((currentUser as any)?.isSystemAdmin);
  const hasSystemAdminRole = projects?.some((p: { role: string }) => p.role === 'SYSTEM_ADMIN') || false;
  const isSystemAdmin = isSystemAdminFromUser || hasSystemAdminRole;
  const hasManagerRole = projects?.some((p: { role: string }) => ['ADMIN', 'MANAGER'].includes(p.role)) || false;
  const isManager = isSystemAdmin || hasManagerRole;
  
  // Шаги: выбор типа -> выбор сотрудников (для регламентных/мероприятий) -> создание дел
  // Для личных чек-листов: выбор типа -> сразу создание
  const [step, setStep] = useState<'select-type' | 'select-users' | 'create-checklist'>('select-type');
  const [selectedAssignees, setSelectedAssignees] = useState<User[]>([]);
  
  // Приёмщики (для руководителей)
  const [approverDailyId, setApproverDailyId] = useState<string>('');
  const [approverWeeklyMonthlyId, setApproverWeeklyMonthlyId] = useState<string>('');
  const [approverEventId, setApproverEventId] = useState<string>('');
  
  // Тип чек-листа: изначально не выбран
  const [type, setType] = useState<'REGULATION' | 'EVENT' | 'PERSONAL' | null>(null);

  // Обновляем начальный шаг когда isManager определится
  useEffect(() => {
    if (!projectsLoading && !userLoading) {
      // Всегда начинаем с выбора типа
      setStep('select-type');
    }
  }, [isManager, projectsLoading, userLoading]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // Получаем подчиненных для руководителей
  // ВАЖНО: Запрос выполняется только если isManager определен (не undefined) и данные загружены
  const { data: subordinates, isLoading: subordinatesLoading, error: subordinatesError } = useQuery({
    queryKey: ['subordinates'],
    queryFn: async () => {
      try {
        const result = await usersApi.getSubordinates();
        return result;
      } catch (error: any) {
        // Игнорируем ошибки 404 и другие - просто возвращаем пустой массив
        if (error?.status === 404 || error?.status === 403) {
          return [];
        }
        throw error;
      }
    },
    enabled: !userLoading && !projectsLoading && Boolean(isManager), // Выполняем только когда данные загружены и isManager определен
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    retry: false, // Не повторяем запрос при ошибке
  });
  
  const addAssignee = (user: User) => {
    if (!selectedAssignees.find(u => u.id === user.id)) {
      setSelectedAssignees([...selectedAssignees, user]);
      hapticFeedback('light');
    }
  };

  const removeAssignee = (userId: string) => {
    setSelectedAssignees(selectedAssignees.filter(u => u.id !== userId));
    hapticFeedback('light');
  };

  // Для руководителей: создание чек-листа с назначением сотрудникам
  const createWithAssigneesMutation = useMutation({
    mutationFn: async () => {
      if (!title.trim()) {
        throw new Error('Укажите название чек-листа');
      }
      if (selectedAssignees.length === 0) {
        throw new Error('Выберите хотя бы одного сотрудника');
      }

      // Создаём пустой чек-лист - правила добавляются позже при создании дел
      return request<any>('/checklist-assignments/create-with-items', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || undefined,
          projectId: projectId || undefined,
          type: type,
          assigneeIds: selectedAssignees.map(u => u.id),
          rules: [],
          items: [],
          approverDailyId: approverDailyId || undefined,
          approverWeeklyMonthlyId: approverWeeklyMonthlyId || undefined,
          approverEventId: approverEventId || undefined,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      hapticFeedback('success');
      showAlert('Чек-лист создан и назначен сотрудникам!');
      navigate('/checklists');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      // Не показываем ошибку "Не найдено" - это техническая ошибка
      if (error?.status === 404) {
        showAlert('Не удалось создать чек-лист. Проверьте подключение к интернету.');
      } else {
        showAlert(error.message || 'Не удалось создать чек-лист');
      }
    },
  });

  // Для обычных сотрудников: создание личного чек-листа (шаблона)
  const createPersonalMutation = useMutation({
    mutationFn: () =>
      checklistsApi.create({
        // Личные чек-листы НЕ должны иметь projectId
        type: 'PERSONAL',
        title,
        description: description || undefined,
        rules: [], // Правила добавляются позже при создании дел
      }),
    onSuccess: async (data) => {
      hapticFeedback('success');
      showAlert('Чек-лист создан');
      
      // Очищаем кеш и обновляем данные
      await queryClient.invalidateQueries({ queryKey: ['checklist-assignments'] });
      await queryClient.invalidateQueries({ queryKey: ['checklists'] });
      await queryClient.invalidateQueries({ queryKey: ['checklist-instances'] });
      
      // Принудительно обновляем все активные запросы
      await queryClient.refetchQueries({ 
        queryKey: ['checklist-assignments'],
        type: 'active'
      });
      
      // Небольшая задержка для завершения транзакции на бэкенде
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      navigate('/checklists');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      // Не показываем ошибку "Не найдено" - это техническая ошибка
      if (error?.status === 404) {
        showAlert('Не удалось создать чек-лист. Проверьте подключение к интернету.');
      } else {
        showAlert(error.message || 'Не удалось создать чек-лист');
      }
    },
  });


  // Обработка выбора типа чек-листа
  const handleTypeSelect = (selectedType: 'REGULATION' | 'EVENT' | 'PERSONAL') => {
    setType(selectedType);
    hapticFeedback('light');
    
    if (selectedType === 'PERSONAL') {
      // Для личного чек-листа сразу переходим к созданию
      setStep('create-checklist');
    } else {
      // Для регламентных и мероприятий - выбор сотрудников
      setStep('select-users');
    }
  };

  const handleNext = () => {
    if (selectedAssignees.length === 0) {
      showAlert('Выберите хотя бы одного сотрудника');
      return;
    }
    setStep('create-checklist');
    hapticFeedback('light');
  };

  const handleBack = () => {
    if (step === 'select-users') {
      // Возвращаемся к выбору типа
      setStep('select-type');
      setType(null);
      setSelectedAssignees([]);
    } else if (step === 'create-checklist') {
      // Возвращаемся к выбору сотрудников (если не личный) или типа
      if (type === 'PERSONAL') {
        setStep('select-type');
        setType(null);
      } else {
        setStep('select-users');
      }
    }
    hapticFeedback('light');
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      showAlert('Укажите название чек-листа');
      return;
    }
    
    // Проверка приёмщиков для регламентных и мероприятий
    if ((type === 'REGULATION' || type === 'EVENT')) {
      if (type === 'REGULATION') {
        if (!approverDailyId) {
          showAlert('Необходимо выбрать приёмщика для ежедневных дел');
          hapticFeedback('error');
          return;
        }
        if (!approverWeeklyMonthlyId) {
          showAlert('Необходимо выбрать приёмщика для еженедельных/ежемесячных дел');
          hapticFeedback('error');
          return;
        }
      } else if (type === 'EVENT') {
        if (!approverEventId) {
          showAlert('Необходимо выбрать приёмщика для мероприятий');
          hapticFeedback('error');
          return;
        }
      }
    }
    
    hapticFeedback('medium');
    
    if (type === 'PERSONAL') {
      // Для личного чек-листа
      createPersonalMutation.mutate();
    } else if (selectedAssignees.length > 0) {
      // Для регламентных и мероприятий: создание с назначением
      createWithAssigneesMutation.mutate();
    } else {
      showAlert('Выберите хотя бы одного сотрудника');
    }
  };

  // Показываем загрузку пока определяем роль
  if (projectsLoading || userLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-tg-hint">Загрузка...</p>
        </div>
      </div>
    );
  }

  // Шаг 1: Выбор типа чек-листа
  if (step === 'select-type') {
    return (
      <div className="p-4 space-y-4 pb-24">
        <div>
          <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
            <Plus className="w-6 h-6" />
            Создать чек-лист
          </h1>
          <p className="text-sm text-tg-hint mt-1">
            Шаг 1: Выберите вид чек-листа
          </p>
        </div>

        <div className="space-y-3">
          {/* Личный чек-лист - доступен всем */}
          <Card 
            variant="interactive"
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => handleTypeSelect('PERSONAL')}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-tg-text">Личный</h3>
                <p className="text-sm text-tg-hint mt-1">
                  Чек-лист для личного использования
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-tg-hint" />
            </div>
          </Card>

          {/* Регламентный чек-лист - только для руководителей */}
          {isManager && (
            <Card 
              variant="interactive"
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => handleTypeSelect('REGULATION')}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-tg-text">Регламентный (для подчиненного)</h3>
                  <p className="text-sm text-tg-hint mt-1">
                    Регулярные дела для сотрудников
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-tg-hint" />
              </div>
            </Card>
          )}

          {/* Мероприятие - только для руководителей */}
          {isManager && (
            <Card 
              variant="interactive"
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => handleTypeSelect('EVENT')}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-tg-text">Мероприятие</h3>
                  <p className="text-sm text-tg-hint mt-1">
                    Чек-лист для события или проекта
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-tg-hint" />
              </div>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Шаг 2: Выбор сотрудников (только для регламентных и мероприятий)
  if (step === 'select-users') {
    return (
      <div className="p-4 space-y-4 pb-24">
        <div>
          <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
            <UserPlus className="w-6 h-6" />
            Создать чек-лист
          </h1>
          <p className="text-sm text-tg-hint mt-1">
            Шаг 2: Выберите сотрудников
          </p>
        </div>

        <Card className="p-4">
          <h2 className="text-lg font-semibold text-tg-text mb-3 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Сотрудники ({selectedAssignees.length})
          </h2>

          {selectedAssignees.length > 0 && (
            <div className="space-y-2 mb-3">
              {selectedAssignees.map((assignee) => (
                <div
                  key={assignee.id}
                  className="flex items-center justify-between p-2 bg-blue-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    {assignee.photoUrl ? (
                      <img
                        src={assignee.photoUrl}
                        alt={assignee.firstName}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                        {assignee.firstName[0]}{assignee.lastName?.[0] || ''}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-tg-text">
                        {assignee.lastName || ''} {assignee.firstName}
                      </p>
                      {assignee.username && (
                        <p className="text-xs text-tg-hint">@{assignee.username}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeAssignee(assignee.id)}
                    className="p-1 text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {subordinatesLoading ? (
            <div className="text-center py-4 text-tg-hint text-sm">Загрузка...</div>
          ) : subordinates && subordinates.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {subordinates
                .filter((u: User) => !selectedAssignees.find((sa: User) => sa.id === u.id))
                .map((user: User) => (
                  <button
                    key={user.id}
                    onClick={() => addAssignee(user)}
                    className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-left"
                  >
                    {user.photoUrl ? (
                      <img
                        src={user.photoUrl}
                        alt={user.firstName}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white text-sm font-semibold">
                        {user.firstName[0]}{user.lastName?.[0] || ''}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-tg-text">
                        {user.lastName || ''} {user.firstName}
                      </p>
                      {user.username && (
                        <p className="text-xs text-tg-hint">@{user.username}</p>
                      )}
                    </div>
                    <Plus className="w-5 h-5 text-blue-500" />
                  </button>
                ))}
              {subordinates.filter((u: User) => !selectedAssignees.find((sa: User) => sa.id === u.id)).length === 0 && (
                <div className="text-center py-4 text-tg-hint text-sm">
                  Все сотрудники уже выбраны
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-tg-hint text-sm">
              Нет доступных сотрудников. Убедитесь, что в проектах есть исполнители (EXECUTOR).
            </div>
          )}
        </Card>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate(-1)}
            className="flex-1"
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={selectedAssignees.length === 0}
            className="flex-1"
          >
            Далее
          </Button>
        </div>
      </div>
    );
  }

  // Шаг 2: Создание чек-листа (для руководителей) или единственный шаг (для обычных сотрудников)
  return (
    <div className="p-4 space-y-4 pb-32 min-h-screen">
      <div>
        <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
          <CheckSquare className="w-6 h-6" />
          {isManager && step === 'create-checklist' ? 'Создать чек-лист' : 'Создать чек-лист'}
        </h1>
        {step === 'create-checklist' && (
          <p className="text-sm text-tg-hint mt-1">
            {type === 'PERSONAL' 
              ? 'Создание личного чек-листа'
              : `Шаг 3: Создание дел для ${selectedAssignees.length} ${selectedAssignees.length === 1 ? 'сотрудника' : 'сотрудников'}`
            }
          </p>
        )}
      </div>

      <div>
        <div className="mb-3">
          <label className="text-sm font-medium text-tg-text mb-3 block">Тип чек-листа</label>
          
          {!isManager && (
            <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-400 rounded-lg shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 mb-1">
                    Ограничение доступа
                  </p>
                  <p className="text-sm text-blue-800 leading-relaxed">
                    Вы можете создавать только <strong>личные чек-листы</strong>. Для создания регламентных чек-листов и мероприятий обратитесь к <strong>непосредственному руководителю</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div 
            className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {(['REGULATION', 'EVENT', 'PERSONAL'] as const)
              .filter(t => isManager || t === 'PERSONAL') // Обычные сотрудники видят только PERSONAL
              .map((t) => (
                <Button
                  key={t}
                  variant={type === t ? 'primary' : 'secondary'}
                  onClick={() => {
                    setType(t);
                    hapticFeedback('light');
                  }}
                  className="whitespace-nowrap flex-shrink-0 px-4"
                  disabled={!isManager && t !== 'PERSONAL'}
                >
                  {t === 'REGULATION' && 'Регламентный'}
                  {t === 'EVENT' && 'Мероприятие'}
                  {t === 'PERSONAL' && 'Личный'}
                </Button>
              ))}
          </div>
        </div>
      </div>

      <Card className="p-4 space-y-4">
        <Input
          label="Название"
          value={title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
          placeholder="Введите название чек-листа"
        />

        <div>
          <label className="text-sm font-medium text-tg-text mb-2 block">Описание</label>
          <textarea
            value={description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
            placeholder="Введите описание (необязательно)"
            className="w-full p-3 border rounded-lg text-sm"
            rows={3}
          />
        </div>

        {/* Настройка приёмщиков (только для руководителей) */}
        {isManager && (type === 'REGULATION' || type === 'EVENT') && (
          <div className="space-y-3 pt-3 border-t">
            <label className="text-sm font-medium text-tg-text block">
              Приёмщики <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-tg-hint mb-3">
              <strong className="text-tg-text">Обязательное условие!</strong> Формируется иерархия приёмки. 
              Если не указаны, будут использованы приёмщики по умолчанию из профилей сотрудников.
            </p>
            
            {type === 'REGULATION' && (
              <>
                <div>
                  <label className="text-xs font-medium text-tg-text mb-1 block">
                    Приёмщик для ежедневных дел <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={approverDailyId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApproverDailyId(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">-- Выберите приёмщика --</option>
                    {subordinatesLoading ? (
                      <option disabled>Загрузка...</option>
                    ) : subordinates && subordinates.length > 0 ? (
                      subordinates.map((user: User) => (
                        <option key={user.id} value={user.id}>
                          {user.lastName || ''} {user.firstName} {user.username ? `(@${user.username})` : ''}
                        </option>
                      ))
                    ) : (
                      <option disabled>Нет доступных пользователей</option>
                    )}
                  </select>
                  {!approverDailyId && (
                    <p className="text-xs text-red-500 mt-1">Необходимо выбрать приёмщика для ежедневных дел</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-tg-text mb-1 block">
                    Приёмщик для еженедельных/ежемесячных дел <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={approverWeeklyMonthlyId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApproverWeeklyMonthlyId(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">-- Выберите приёмщика --</option>
                    {subordinatesLoading ? (
                      <option disabled>Загрузка...</option>
                    ) : subordinates && subordinates.length > 0 ? (
                      subordinates.map((user: User) => (
                        <option key={user.id} value={user.id}>
                          {user.lastName || ''} {user.firstName} {user.username ? `(@${user.username})` : ''}
                        </option>
                      ))
                    ) : (
                      <option disabled>Нет доступных пользователей</option>
                    )}
                  </select>
                  {!approverWeeklyMonthlyId && (
                    <p className="text-xs text-red-500 mt-1">Необходимо выбрать приёмщика для еженедельных/ежемесячных дел</p>
                  )}
                </div>
              </>
            )}
            
            {type === 'EVENT' && (
              <div>
                <label className="text-xs font-medium text-tg-text mb-1 block">
                  Приёмщик для мероприятий <span className="text-red-500">*</span>
                </label>
                <select
                  value={approverEventId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApproverEventId(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                  required
                >
                  <option value="">-- Выберите приёмщика --</option>
                  {subordinatesLoading ? (
                    <option disabled>Загрузка...</option>
                  ) : subordinates && subordinates.length > 0 ? (
                    subordinates.map((user: User) => (
                      <option key={user.id} value={user.id}>
                        {user.lastName || ''} {user.firstName} {user.username ? `(@${user.username})` : ''}
                      </option>
                    ))
                  ) : (
                    <option disabled>Нет доступных пользователей</option>
                  )}
                </select>
                {!approverEventId && (
                  <p className="text-xs text-red-500 mt-1">Необходимо выбрать приёмщика для мероприятий</p>
                )}
              </div>
            )}
          </div>
        )}
      </Card>


      <div className="flex gap-2 sticky bottom-0 bg-white pt-4 pb-4 -mx-4 px-4 border-t border-gray-200">
        {isManager && step === 'create-checklist' ? (
          <>
            <Button
              variant="secondary"
              onClick={handleBack}
              className="flex-1"
            >
              Назад
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={createWithAssigneesMutation.isPending}
              className="flex-1"
            >
              Создать
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              className="flex-1"
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={createPersonalMutation.isPending}
              className="flex-1"
            >
              Создать
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

